import {
	useEffect,
	useRef,
	createContext,
	type ReactNode,
	useMemo,
	useContext,
	useState,
	useCallback,
} from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { updateFeatureGatesClientWithUserIdentifiers } from '@/feature-gates/initialization.tsx';

import { useCloudStatus } from './use-cloud-status';

const TenantAwareStatsigClientUpdater = createContext<{
	updatedStatsigClientWithCloudId: boolean;
}>({
	updatedStatsigClientWithCloudId: false,
});

export const useTenantAwareStatsigClientUpdater = () => {
	const context = useContext(TenantAwareStatsigClientUpdater);
	return context;
};

export const TenantAwareStatsigClientUpdaterProvider = ({
	children,
}: {
	children: ReactNode | undefined;
}) => {
	const [updatedStatsigClientWithCloudId, setUpdatedStatsigClientWithCloudId] = useState(false);
	const updateCalledOnce = useRef(false);

	const { cloudId } = useCloudStatus();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const updateStatsigClient = useCallback(async () => {
		if (
			typeof window !== 'undefined' &&
			!window.featureGateBootstrapValues.identifiers.tenantId &&
			window.featureGateBootstrapValues
		) {
			try {
				await updateFeatureGatesClientWithUserIdentifiers(window.featureGateBootstrapValues, {
					tenantId: cloudId,
				});
			} catch (error) {
				createAnalyticsEvent({
					action: 'failed',
					actionSubject: 'TenantAwareStatsigClientUpdaterProvider.updateStatsigClient',
					eventType: 'operational',
					attributes: {
						error: error instanceof Error ? error.message : '',
					},
				}).fire();
			}
		}
		setUpdatedStatsigClientWithCloudId(true);
	}, [cloudId, createAnalyticsEvent]);

	useEffect(() => {
		if (!updateCalledOnce.current && cloudId) {
			updateCalledOnce.current = true;
			updateStatsigClient();
		}
	}, [cloudId, updateStatsigClient]);

	const value = useMemo(
		() => ({
			updatedStatsigClientWithCloudId: updatedStatsigClientWithCloudId,
		}),
		[updatedStatsigClientWithCloudId],
	);

	return (
		<TenantAwareStatsigClientUpdater.Provider value={value}>
			{children}
		</TenantAwareStatsigClientUpdater.Provider>
	);
};
